import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { Badge, Banner, Button, Card, Spinner } from "@magarac/ui";
import { MODULE_LABELS, MODULES } from "@magarac/shared/billing";
import { formatCents } from "@magarac/shared/money";
import { accountBillingSchema, type AccountBilling } from "@magarac/shared/schemas/billing";
import { z } from "zod";
import { CancelAccountCard } from "../../../components/CancelAccountCard";
import { HelpIcon } from "../../../components/HelpIcon";
import { api } from "../../../lib/api";
import { dayText } from "../../../lib/documents";
import { messageFor } from "../../../lib/errors";
import { useTenant } from "../../../lib/tenant";

export const Route = createFileRoute("/_app/settings/plan")({
  component: PlanPage,
});

/** BIL-8: plan, modules, seats, next charge, and payment status. Screen key: settings.plan */
function PlanPage() {
  const { active } = useTenant();
  const q = useQuery({
    queryKey: ["billing", active?.id],
    queryFn: () => api.get("/account/billing", accountBillingSchema),
    enabled: Boolean(active),
  });
  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Plan and billing</h1>
        <HelpIcon screen="settings.plan" />
      </div>
      {q.isPending ? (
        <Spinner />
      ) : q.error ? (
        <Banner tone="danger">{messageFor(q.error)}</Banner>
      ) : (
        <PlanView b={q.data} isOwner={active?.role === "owner"} />
      )}
    </div>
  );
}

function PlanView({ b, isOwner }: { b: AccountBilling; isOwner: boolean }) {
  const { active } = useTenant();
  const queryClient = useQueryClient();
  const [notice, setNotice] = useState<string | null>(null);
  const trim = useMutation({
    mutationFn: () =>
      api.post(
        "/account/billing/seats",
        { seats: Math.max(b.seatsUsed, b.includedSeats) },
        z.object({ seats: z.number() }),
      ),
    onSuccess: async ({ seats }) => {
      setNotice(`Done. You now pay for ${seats} seats.`);
      await queryClient.invalidateQueries({ queryKey: ["billing", active?.id] });
    },
  });
  const unused = b.seats !== null ? b.seats - Math.max(b.seatsUsed, b.includedSeats) : 0;

  return (
    <>
      {b.businessStatus === "past_due" ? (
        <Banner tone="danger">
          Your last payment to J360 did not go through. Everything still works, but if it is not
          paid{b.readOnlyOn ? ` by ${dayText(b.readOnlyOn)}` : " soon"}, your account becomes
          view-only.
        </Banner>
      ) : null}
      {b.businessStatus === "suspended" && b.pastDueSince ? (
        <Banner tone="danger">
          Your account is view-only because payment to J360 is overdue. Paying brings everything
          back right away.
        </Banner>
      ) : null}
      {notice ? <Banner tone="ok">{notice}</Banner> : null}
      {trim.error ? <Banner tone="danger">{messageFor(trim.error)}</Banner> : null}

      <Card title="Your plan" className="mb-6">
        <p className="mb-1 text-lg font-semibold">{b.planName}</p>
        {b.comp ? (
          <p className="text-sm text-muted">
            Your account was set up by the J360 team with everything included. We will talk with you
            before any plan starts.
          </p>
        ) : (
          <p className="text-sm text-muted">
            {formatCents(b.monthlyChargeCents)} a month
            {b.currentPeriodEnd ? `, next charge ${dayText(b.currentPeriodEnd)}` : ""}.
            {b.provider === "fake" ? " (Test mode: no card is charged.)" : ""}
          </p>
        )}
        <ul className="mt-4 grid gap-1 sm:grid-cols-2">
          {MODULES.map((m) => (
            <li key={m} className="flex items-center gap-2">
              <Badge tone={b.modules.includes(m) ? "ok" : "neutral"}>
                {b.modules.includes(m) ? "Included" : "Not included"}
              </Badge>
              {MODULE_LABELS[m]}
            </li>
          ))}
        </ul>
      </Card>

      <Card title="Seats" className="mb-6">
        {b.seats === null ? (
          <p>
            Your plan is not billed per person. {b.seatsUsed} people have logins or open
            invitations.
          </p>
        ) : (
          <>
            <p>
              {b.seatsUsed} of {b.seats} seats in use ({b.includedSeats} come with the plan; extra
              seats are {formatCents(b.seatPriceCents)} a month each).
            </p>
            <p className="mt-1 text-sm text-muted">
              People with a login and invitations not yet accepted each use a seat. Inviting past
              your seats asks first, then adds one.
            </p>
            {unused > 0 && isOwner ? (
              <Button
                variant="secondary"
                className="mt-3"
                busy={trim.isPending}
                onClick={() => trim.mutate()}
              >
                Stop paying for {unused} unused seat{unused === 1 ? "" : "s"}
              </Button>
            ) : null}
          </>
        )}
      </Card>

      <Card title="Payment method and invoices">
        <p className="text-sm text-muted">
          {b.provider === "stripe"
            ? "Change your card or download invoices from Stripe."
            : "Card changes and J360 invoices appear here once billing is switched on."}
        </p>
        <p className="mt-3 text-sm">
          Card payments from your customers: {b.cardPaymentsConnected ? "on" : "not set up"} (see
          Settings &gt; Payments).
        </p>
      </Card>

      <div className="mt-6">
        <CancelAccountCard b={b} isOwner={isOwner} />
      </div>
    </>
  );
}
